
import { isEmail } from 'bits/email'
import { PiniaMapState } from 'bits/pinia'
import { Component, Prop, Vue } from 'bits/vue'
import { useDarkModeStore } from 'pinia/dark_mode'

type InputFieldRules = { required?: boolean; minLength?: number; email?: boolean }

/**
 * @displayName Base Text Input
 */

@Component({})
export default class ContactFormTextInput extends Vue {
  @PiniaMapState(useDarkModeStore) isDarkMode!: ReturnType<typeof useDarkModeStore>['isDarkMode']

  /**
   * Determines label name
   */
  @Prop({ default: '', required: true }) name!: string
  /**
   * Insert id to bind label/input, which will enable autofocus
   * on input field when label is clicked. This value will also be used to locate the
   * correct input element that is being updated.
   */
  @Prop() id!: string
  /**
   * Bind input value attribute with value prop
   */
  @Prop() value!: string
  /**
   * Insert placeholder text for input field
   */
  @Prop({ default: '' }) placeholder!: string
  /**
   * Insert rules for input.
   * Supported rules are: required, minLength, email
   */
  @Prop({ default: () => ({ required: false }) }) rules!: InputFieldRules
  /**
   * Insert type of input
   * @values text, password
   */
  @Prop({ default: 'text' }) type!: 'text' | 'password'

  get error(): string | undefined | void {
    return this.validate(this.value)
  }

  get requiredLabel(): string {
    return this.rules.required ? `${this.name} *` : this.name
  }

  get isRequired(): boolean {
    return !!this.rules.required
  }

  get wrapperClass(): object {
    return {
      'dark-mode': this.isDarkMode,
      'input-wrapper': true,
    }
  }

  input(ev): void {
    this.validate(ev.target.value)
    /**
     * Triggers when the input value changes
     *
     * @property {string} id Data required to specify which input value it's updating
     * @property {string} name Name that was set before the change
     * @property {string} value Data that updates new input value (Updates parent state with value)
     */
    this.$emit('update', {
      id: this.id,
      name: this.name,
      value: ev.target.value,
    })
  }

  validateEmail(email: string, element: HTMLInputElement): void {
    const validEmail = isEmail(email)

    // Set email validation error message
    if (!validEmail && element) {
      element.setCustomValidity(this.__('Please enter valid email'))
    } else {
      element.setCustomValidity('')
    }
  }

  validate(value): string | undefined | void {
    // Show error messages based on rules prop
    const inputElement = this.$refs[this.id] as HTMLInputElement

    if (inputElement) {
      if (this.rules.minLength && value.length < this.rules.minLength) {
        const errorMessage = this.__('Minimum length is %{minimumLength}', { minimumLength: this.rules.minLength })
        inputElement.setCustomValidity(errorMessage)
      } else if (this.rules.email) {
        return this.validateEmail(value, inputElement)
      } else {
        inputElement.setCustomValidity('')
      }
    }
  }
}
